import { useEffect, useMemo } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { generateMonthsObjects, getDateRangeFromYears } from '../utils';

export interface ExportPoleOption {
  value: string,
  isPole?: boolean,
}

export interface ExportMonthOption {
  value: string | null,
  label: string,
  start?: string,
}

interface UseExportYearPeriodParams {
  watch: UseFormWatch<Record<string, unknown>>,
  setValue: UseFormSetValue<Record<string, unknown>>,
  exportList: Record<string, unknown> | null | undefined,
  hasStructure?: boolean,
}

function useExportYearPeriod({
  watch,
  setValue,
  exportList,
  hasStructure = true,
}: UseExportYearPeriodParams) {
  const form = watch();

  const options = useMemo(() => {
    const opt: {
      users: unknown[],
      poles: ExportPoleOption[],
      startDate: ExportMonthOption[],
      endDate: ExportMonthOption[],
    } = {
      users: [],
      poles: [],
      startDate: [{ value: null, label: 'Séléctionner' }],
      endDate: [{ value: null, label: 'Séléctionner' }],
    };

    if (hasStructure && form.structure && exportList?.users) {
      opt.users = (exportList.users as { structure: string }[])
        .filter((u) => u.structure === form.structure);
      const poles = (exportList.structures as { value: string, poles?: ExportPoleOption[] }[])
        ?.find((u) => u.value === form.structure)?.poles;
      if (poles?.length) {
        opt.poles = [...poles];
      }
    }

    const years = form.years as number[] | undefined;
    if (years?.length) {
      const { startDate, endDate } = getDateRangeFromYears(years);
      const months = generateMonthsObjects(new Date(startDate), new Date(endDate));
      months.forEach((m) => {
        opt.startDate.push({ value: m.start, label: m.month });
        opt.endDate.push({ value: m.end, label: m.month, start: m.start });
      });
      const today = new Date();
      const current = months.findIndex(
        (m) => new Date(m.start).getMonth() === today.getMonth()
          && new Date(m.start).getFullYear() === today.getFullYear(),
      );
      const currentIndex = current >= 0 ? current : 0;
      if (!form.endDate) {
        setValue('endDate', months[currentIndex]?.end);
      }
      if (!form.startDate) {
        setValue('startDate', months[currentIndex]?.start);
      }
    }

    if (form.startDate) {
      opt.endDate = [
        { value: null, label: 'Séléctionner' },
        ...opt.endDate.filter(
          (w) => w.start && new Date(w.start) >= new Date(form.startDate as string),
        ),
      ];
    }

    return opt;
  }, [
    exportList,
    form.structure,
    form.years,
    form.endDate,
    form.startDate,
    hasStructure,
    setValue,
  ]);

  useEffect(() => {
    if ((form.years as number[] | undefined)?.length) {
      setValue('endDate', null);
      setValue('startDate', null);
    }
  }, [form.years, setValue]);

  return { options };
}

export default useExportYearPeriod;
